// ============================================================================
// pdf-eof-repair.js - the one repair an upload makes to a document before the
// server's byte check: a reference PDF cut off at %%EO gets its missing final F
// (v5.65.20, a real Customer Zero file, the Bondi analytical report).
// ----------------------------------------------------------------------------
// Moved unchanged out of file-upload-service.js so the Job import judges the
// same bytes the upload sends (import:F7): uploadFileToS3 runs it before it
// registers the bytes, and quote-import/read-document.ts runs it before it
// decides whether the original can be stored. src/__tests__/
// file-upload-service.test.js pins the repair itself.
// ============================================================================

const PDF_HEADER_BYTES = Object.freeze([0x25, 0x50, 0x44, 0x46, 0x2d]); // %PDF-
const TRUNCATED_PDF_EOF_BYTES = Object.freeze([0x25, 0x25, 0x45, 0x4f]); // %%EO
const COMPLETE_PDF_EOF_BYTES = Object.freeze([0x25, 0x25, 0x45, 0x4f, 0x46]); // %%EOF
const PDF_BYTE_RANGE_BYTES = Object.freeze([0x2f, 0x42, 0x79, 0x74, 0x65, 0x52, 0x61, 0x6e, 0x67, 0x65]); // /ByteRange
const PDF_REPAIR_SCAN_CHUNK_BYTES = 4096;
const PDF_REPAIR_AMBIGUITY_CHUNK_BYTES = 1024 * 1024;
const REPAIRABLE_REFERENCE_DOCUMENT_LANES = new Set([
    'project_document',
    // Intake PDFs are source references too. Photo/video bytes in this mixed
    // lane remain excluded by the application/pdf + PDF-signature checks.
    'intake_visit_media',
]);

function bytesEqual(bytes, expected) {
    return bytes.length === expected.length
        && expected.every((value, index) => bytes[index] === value);
}

function isPdfWhitespaceByte(byte) {
    // ISO 32000 whitespace: NUL, horizontal tab, line feed, form feed,
    // carriage return, and space.
    return byte === 0x00
        || byte === 0x09
        || byte === 0x0a
        || byte === 0x0c
        || byte === 0x0d
        || byte === 0x20;
}

function bytesContain(bytes, expected) {
    if (expected.length === 0 || bytes.length < expected.length) return false;
    outer: for (let offset = 0; offset <= bytes.length - expected.length; offset += 1) {
        for (let index = 0; index < expected.length; index += 1) {
            if (bytes[offset + index] !== expected[index]) continue outer;
        }
        return true;
    }
    return false;
}

async function blobContainsPdfRepairAmbiguity(blob, endExclusive) {
    const blockers = [COMPLETE_PDF_EOF_BYTES, PDF_BYTE_RANGE_BYTES];
    const overlapSize = Math.max(...blockers.map((value) => value.length)) - 1;
    let carry = new Uint8Array();
    for (let offset = 0; offset < endExclusive; offset += PDF_REPAIR_AMBIGUITY_CHUNK_BYTES) {
        const chunkEnd = Math.min(endExclusive, offset + PDF_REPAIR_AMBIGUITY_CHUNK_BYTES);
        const chunk = new Uint8Array(await blob.slice(offset, chunkEnd).arrayBuffer());
        const window = new Uint8Array(carry.length + chunk.length);
        window.set(carry);
        window.set(chunk, carry.length);
        if (blockers.some((pattern) => bytesContain(window, pattern))) return true;
        carry = window.slice(Math.max(0, window.length - overlapSize));
    }
    return false;
}

function bytesToAscii(bytes) {
    return String.fromCharCode(...bytes);
}

/**
 * The bytes an upload on `entityType` sends for this blob: the blob itself, or
 * for a reference-document PDF that ends in %%EO, allowing trailing PDF
 * whitespace (strict version line, a startxref that points at an xref, no
 * earlier %%EOF and no /ByteRange), a new Blob with the missing F added.
 *
 * @param {Blob} blob
 * @param {string} contentType
 * @param {string} entityType
 * @returns {Promise<Blob>}
 */
export async function repairSingleMissingPdfEofByte(blob, contentType, entityType) {
    if (!REPAIRABLE_REFERENCE_DOCUMENT_LANES.has(entityType)
        || String(contentType || '').split(';', 1)[0].trim().toLowerCase() !== 'application/pdf'
        || blob.size < PDF_HEADER_BYTES.length + TRUNCATED_PDF_EOF_BYTES.length) {
        return blob;
    }

    const header = new Uint8Array(await blob.slice(0, 10).arrayBuffer());
    const strictVersionLine = bytesEqual(header.subarray(0, PDF_HEADER_BYTES.length), PDF_HEADER_BYTES)
        && (header[5] === 0x31 || header[5] === 0x32)
        && header[6] === 0x2e
        && header[7] >= 0x30
        && header[7] <= 0x39
        && (header[8] === 0x0a || header[8] === 0x0d);
    if (!strictVersionLine) return blob;

    // Read backwards in bounded chunks so large PDFs keep the streaming upload
    // path. The only accepted repair is one missing final `F` after an exact
    // terminal `%%EO`; every other malformed tail remains unchanged for the
    // backend's strict document validator to reject.
    let scanEnd = blob.size;
    while (scanEnd > 0) {
        const scanStart = Math.max(0, scanEnd - PDF_REPAIR_SCAN_CHUNK_BYTES);
        const tail = new Uint8Array(await blob.slice(scanStart, scanEnd).arrayBuffer());
        let lastContentOffset = tail.length;
        while (lastContentOffset > 0 && isPdfWhitespaceByte(tail[lastContentOffset - 1])) {
            lastContentOffset -= 1;
        }
        if (lastContentOffset === 0) {
            scanEnd = scanStart;
            continue;
        }

        const terminalEnd = scanStart + lastContentOffset;
        if (terminalEnd < TRUNCATED_PDF_EOF_BYTES.length) return blob;
        const terminalStart = terminalEnd - TRUNCATED_PDF_EOF_BYTES.length;
        const terminal = terminalStart >= scanStart
            ? tail.subarray(terminalStart - scanStart, lastContentOffset)
            : new Uint8Array(await blob.slice(terminalStart, terminalEnd).arrayBuffer());
        if (!bytesEqual(terminal, TRUNCATED_PDF_EOF_BYTES)) return blob;

        const trailerStart = Math.max(0, terminalEnd - PDF_REPAIR_SCAN_CHUNK_BYTES);
        const trailer = new Uint8Array(await blob.slice(trailerStart, terminalEnd).arrayBuffer());
        const startxrefMatch = bytesToAscii(trailer).match(
            /startxref[\x00\t\n\f\r ]+([0-9]+)[\x00\t\n\f\r ]+%%EO$/,
        );
        if (!startxrefMatch) return blob;
        const startxref = Number(startxrefMatch[1]);
        if (!Number.isSafeInteger(startxref) || startxref < 0 || startxref >= terminalEnd) return blob;
        const xrefTargetBytes = new Uint8Array(
            await blob.slice(startxref, Math.min(terminalEnd, startxref + 64)).arrayBuffer(),
        );
        const xrefTarget = bytesToAscii(xrefTargetBytes);
        const plausibleXrefTarget = /^(?:xref(?:[\x00\t\n\f\r ]|$)|[0-9]+[\x00\t\n\f\r ]+[0-9]+[\x00\t\n\f\r ]+obj(?:[\x00\t\n\f\r ]|$))/.test(xrefTarget);
        if (!plausibleXrefTarget) return blob;

        // Do not alter signed or incremental PDFs. A prior complete EOF means
        // the damaged suffix may be a later revision; /ByteRange means one byte
        // changes signature semantics. Both stay fail-closed for human repair.
        if (await blobContainsPdfRepairAmbiguity(blob, terminalEnd)) return blob;

        return new Blob([
            blob.slice(0, terminalEnd),
            new Uint8Array([0x46]), // F
            blob.slice(terminalEnd),
        ], { type: 'application/pdf' });
    }

    return blob;
}
